import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiX, FiSave, FiLoader, FiEye, FiEyeOff, FiShield, FiGlobe, FiCreditCard } = FiIcons;

const PaymentGatewayConfig = ({ isOpen, onClose, gateway, onSave }) => {
  const [showSecrets, setShowSecrets] = useState({});
  const [activeTab, setActiveTab] = useState('credentials');

  const { register, handleSubmit, formState: { errors }, reset, watch } = useForm({
    defaultValues: gateway || {}
  });

  const toggleSecretVisibility = (field) => {
    setShowSecrets(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleFormSubmit = async (data) => {
    await onSave(gateway.id, data);
    onClose();
  };

  const renderCredentialsTab = () => (
    <div className="space-y-6">
      {/* API Keys Section */}
      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
        <h4 className="font-semibold text-gray-800 mb-4 flex items-center">
          <SafeIcon icon={FiShield} className="w-5 h-5 mr-2 text-blue-600" />
          API Credentials
        </h4>
        
        <div className="grid grid-cols-1 gap-4">
          {gateway.id === 'stripe' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Publishable Key (Test) *
                </label>
                <div className="relative">
                  <input
                    type={showSecrets.publishable_key_test ? 'text' : 'password'}
                    {...register('config.publishable_key_test', { required: 'Required' })}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="pk_test_..."
                  />
                  <button
                    type="button"
                    onClick={() => toggleSecretVisibility('publishable_key_test')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                  >
                    <SafeIcon icon={showSecrets.publishable_key_test ? FiEyeOff : FiEye} className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
                {errors.config?.publishable_key_test && (
                  <p className="text-red-500 text-sm mt-1">This field is required</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Secret Key (Test) *
                </label>
                <div className="relative">
                  <input
                    type={showSecrets.secret_key_test ? 'text' : 'password'}
                    {...register('config.secret_key_test', { required: 'Required' })}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="sk_test_..."
                  />
                  <button
                    type="button"
                    onClick={() => toggleSecretVisibility('secret_key_test')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                  >
                    <SafeIcon icon={showSecrets.secret_key_test ? FiEyeOff : FiEye} className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Webhook Endpoint
                </label>
                <input
                  type="url"
                  {...register('config.webhook_endpoint')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="https://yoursite.com/webhooks/stripe"
                />
              </div>
            </>
          )}

          {gateway.id === 'razorpay' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Key ID (Test) *
                </label>
                <div className="relative">
                  <input
                    type={showSecrets.key_id_test ? 'text' : 'password'}
                    {...register('config.key_id_test', { required: 'Required' })}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="rzp_test_..."
                  />
                  <button
                    type="button"
                    onClick={() => toggleSecretVisibility('key_id_test')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                  >
                    <SafeIcon icon={showSecrets.key_id_test ? FiEyeOff : FiEye} className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Key Secret (Test) *
                </label>
                <div className="relative">
                  <input
                    type={showSecrets.key_secret_test ? 'text' : 'password'}
                    {...register('config.key_secret_test', { required: 'Required' })}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="key_secret_..."
                  />
                  <button
                    type="button"
                    onClick={() => toggleSecretVisibility('key_secret_test')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                  >
                    <SafeIcon icon={showSecrets.key_secret_test ? FiEyeOff : FiEye} className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Webhook Secret
                </label>
                <div className="relative">
                  <input
                    type={showSecrets.webhook_secret ? 'text' : 'password'}
                    {...register('config.webhook_secret')}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="whsec_..."
                  />
                  <button
                    type="button"
                    onClick={() => toggleSecretVisibility('webhook_secret')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                  >
                    <SafeIcon icon={showSecrets.webhook_secret ? FiEyeOff : FiEye} className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
              </div>
            </>
          )}

          {gateway.id === 'paypal' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Client ID (Sandbox) *
                </label>
                <div className="relative">
                  <input
                    type={showSecrets.client_id_sandbox ? 'text' : 'password'}
                    {...register('config.client_id_sandbox', { required: 'Required' })}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Client ID..."
                  />
                  <button
                    type="button"
                    onClick={() => toggleSecretVisibility('client_id_sandbox')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                  >
                    <SafeIcon icon={showSecrets.client_id_sandbox ? FiEyeOff : FiEye} className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Client Secret (Sandbox) *
                </label>
                <div className="relative">
                  <input
                    type={showSecrets.client_secret_sandbox ? 'text' : 'password'}
                    {...register('config.client_secret_sandbox', { required: 'Required' })}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Client Secret..."
                  />
                  <button
                    type="button"
                    onClick={() => toggleSecretVisibility('client_secret_sandbox')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                  >
                    <SafeIcon icon={showSecrets.client_secret_sandbox ? FiEyeOff : FiEye} className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Security Notice */}
      <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
        <div className="flex items-start space-x-2">
          <SafeIcon icon={FiShield} className="w-5 h-5 text-yellow-600 mt-0.5" />
          <div>
            <h5 className="font-medium text-yellow-800">Security Notice</h5>
            <p className="text-sm text-yellow-700 mt-1">
              Your API keys are encrypted and stored securely. Never share these credentials with unauthorized parties.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSettingsTab = () => (
    <div className="space-y-6">
      {/* Payment Methods */}
      <div className="bg-green-50 rounded-lg p-4 border border-green-200">
        <h4 className="font-semibold text-gray-800 mb-4 flex items-center">
          <SafeIcon icon={FiCreditCard} className="w-5 h-5 mr-2 text-green-600" />
          Enabled Payment Methods
        </h4>
        
        <div className="grid grid-cols-2 gap-3">
          {gateway.config.payment_methods?.map((method) => (
            <label key={method} className="flex items-center space-x-2">
              <input
                type="checkbox"
                defaultChecked
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 capitalize">{method.replace('_', ' ')}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Currency Settings */}
      <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
        <h4 className="font-semibold text-gray-800 mb-4 flex items-center">
          <SafeIcon icon={FiGlobe} className="w-5 h-5 mr-2 text-purple-600" />
          Supported Currencies
        </h4>
        
        <div className="grid grid-cols-3 gap-3">
          {gateway.config.supported_currencies?.map((currency) => (
            <label key={currency} className="flex items-center space-x-2">
              <input
                type="checkbox"
                defaultChecked
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">{currency}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Additional Settings */}
      <div className="space-y-4">
        <div>
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              {...register('auto_capture')}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Auto-capture payments</span>
          </label>
          <p className="text-xs text-gray-500 ml-6">Automatically capture authorized payments</p>
        </div>

        <div>
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              {...register('send_receipts')}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Send email receipts</span>
          </label>
          <p className="text-xs text-gray-500 ml-6">Automatically send receipts to customers</p>
        </div>

        <div>
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              {...register('save_payment_methods')}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Save payment methods</span>
          </label>
          <p className="text-xs text-gray-500 ml-6">Allow customers to save cards for future payments</p>
        </div>
      </div>
    </div>
  );

  const tabs = [
    { id: 'credentials', label: 'API Credentials', icon: FiShield },
    { id: 'settings', label: 'Settings', icon: FiGlobe }
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          
          <motion.div
            className="relative bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-xl z-10">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-800">Configure {gateway?.name}</h2>
                  <p className="text-sm text-gray-600">Set up payment gateway credentials and preferences</p>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                >
                  <SafeIcon icon={FiX} className="w-5 h-5" />
                </button>
              </div>

              {/* Tabs */}
              <div className="flex space-x-1 mt-4">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors duration-200 ${
                      activeTab === tab.id
                        ? 'bg-blue-100 text-blue-700'
                        : 'hover:bg-gray-100 text-gray-600'
                    }`}
                  >
                    <SafeIcon icon={tab.icon} className="w-4 h-4" />
                    <span className="text-sm font-medium">{tab.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <form onSubmit={handleSubmit(handleFormSubmit)} className="p-6">
              {activeTab === 'credentials' && renderCredentialsTab()}
              {activeTab === 'settings' && renderSettingsTab()}

              <div className="flex space-x-3 pt-6 border-t border-gray-200 mt-6">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200 font-medium flex items-center justify-center space-x-2"
                >
                  <SafeIcon icon={FiSave} className="w-4 h-4" />
                  <span>Save Configuration</span>
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default PaymentGatewayConfig;